import { useMemo, useRef } from 'react';

import { Button } from '@/components/ui/button';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { TranscriptEntryList } from './TranscriptEntryList';
import { useArrivals } from '../hooks/useArrivals';
import { useExecutionTranscript } from '../hooks/useExecutionTranscript';
import { useTailFollow } from '../hooks/useTailFollow';
import { splitTranscriptPhases } from '../transcript';
import { editedWorkspacePaths } from '../requestedEdit';
import {
  filePreviewLink,
  useWorkspaceFileExistence,
} from '@/features/workspace-files';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { RetryButton } from '@/shared/components/RetryButton';

export interface ExecutionDetailProps {
  workspaceId: string;
  taskId: string;
  executionId: string;
  /** Whether this run's row is expanded. Nothing is fetched until it is. */
  isOpen: boolean;
  /** Whether the run has ended — a finished run's transcript never changes again. */
  isFinished: boolean;
  /** This execution's configured turn limit (spec 102), from its `TaskExecutionSummary` — not carried by the transcript itself. */
  maxTurns?: number | null;
}

/**
 * The expanded content for one execution row: its transcript, fetched on open.
 *
 * Transcripts used to arrive with the workspace aggregate, so this component had
 * nothing to do but render what it was handed. Spec 081 took them off that read — it
 * is polled every two seconds by every open board and no board surface renders a
 * transcript — so the fetch lives here, where the thing is actually shown.
 *
 * Three states around it, per spec 088: a placeholder that waits out the appearance
 * delay rather than flashing, a failure stated plainly with a retry, and the content.
 * The failure is contained to this panel and never reaches the board around it
 * (FR-011) — a run whose transcript will not load is not a broken board.
 *
 * The sticky run header that names this run while it scrolls (spec 096 FR-010)
 * is the rail's, rendered just before this panel — see `RunHeader` for why it
 * cannot live inside a clipping panel.
 *
 * `prompt_snapshot` is fetched with it and rendered above it by `RunPrompt`
 * (spec 149), which reads this same query when opened.
 */
export function ExecutionDetail({
  workspaceId,
  taskId,
  executionId,
  isOpen,
  isFinished,
  maxTurns,
}: ExecutionDetailProps) {
  const { transcript, isPending, loadFailed, retry, isFetching } =
    useExecutionTranscript(workspaceId, taskId, executionId, {
      enabled: isOpen,
      isFinished,
    });
  // Paired with `isOpen`: an unenabled query reports `isPending` forever, so on its own
  // it would mean "loading" for every collapsed run on the board.
  const showPlaceholder = usePendingDisplay(isOpen && isPending);

  /*
    Spec 097: while the run is live the transcript grows under the reader. Three
    things follow it — which entries are new (the arrival cue and the polite
    announcement), whether the view should move (tail following), and where
    the list is in the drawer's scroller (for that following). The main-phase
    count is what grows; the transition-selection phase only ever appears at
    the end, once.
  */
  const live = !isFinished;
  const listRef = useRef<HTMLDivElement>(null);
  const mainCount = transcript
    ? splitTranscriptPhases(transcript).main.length
    : 0;
  const arrivals = useArrivals(mainCount, !isPending);
  const { pending, jumpToTail } = useTailFollow(listRef, mainCount, live);

  // The files this run's edits name, checked against the workspace the same way
  // paths in prose are (spec 157 FR-007): linked only while the file is there.
  // A transcript without edits asks about nothing, so it makes no request.
  const editedPaths = useMemo(
    () => editedWorkspacePaths(transcript ?? []),
    [transcript],
  );
  const existence = useWorkspaceFileExistence(workspaceId, editedPaths);
  const filePaths: MarkdownFilePaths = {
    exists: existence.exists,
    linkTo: (path) => filePreviewLink(workspaceId, path),
  };

  if (isPending) {
    return showPlaceholder ? (
      <p
        className="text-muted-foreground text-sm"
        data-testid="transcript-state"
      >
        Loading transcript…
      </p>
    ) : null;
  }

  if (loadFailed && transcript === undefined) {
    return (
      <div
        className="flex flex-col items-start gap-2"
        data-testid="transcript-state"
      >
        <p className="text-muted-foreground text-sm">
          This run&rsquo;s transcript could not be loaded.
        </p>
        <RetryButton onRetry={retry} isFetching={isFetching} />
      </div>
    );
  }

  // A live run with nothing stored yet is waiting, not lost: 089's "unavailable"
  // is about a finished run whose record never came back (FR-007).
  if (live && transcript === null && !loadFailed) {
    return (
      <p
        className="text-muted-foreground text-sm"
        data-testid="transcript-state"
      >
        Waiting for the first turn…
      </p>
    );
  }

  return (
    <div ref={listRef} className="relative flex flex-col gap-3">
      <TranscriptEntryList
        transcript={transcript}
        arrivedFrom={live ? arrivals.arrivedFrom : null}
        maxTurns={maxTurns}
        filePaths={filePaths}
      />
      {loadFailed ? (
        // A refresh that failed keeps what was already read (FR-004); the
        // failure is said beneath it, not instead of it.
        <div
          className="flex flex-wrap items-center gap-2"
          data-testid="transcript-state"
        >
          <p className="text-muted-foreground text-sm">
            The latest turns could not be loaded.
          </p>
          <RetryButton onRetry={retry} isFetching={isFetching} />
        </div>
      ) : null}
      {pending > 0 ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shadow-raised sticky bottom-2 z-10 self-center"
          onClick={jumpToTail}
        >
          {pending} new {pending === 1 ? 'entry' : 'entries'}
        </Button>
      ) : null}
      <p aria-live="polite" className="sr-only">
        {live && arrivals.delta > 0
          ? `${String(arrivals.delta)} new ${arrivals.delta === 1 ? 'entry' : 'entries'}`
          : ''}
      </p>
    </div>
  );
}
